import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  AttendanceTime,
  Badge,
  Button,
  EmptyState,
  InfoBox,
  LoadingState,
  kstDateStr,
  kstMonthDayStr,
  kstTimeStr,
  type AttendanceKind,
} from '@biznomi/ui-react'
import { manualReasonLabel, isAbsenceRecord, attendanceTypeLabel } from '@biznomi/types'
import { getMyAttendance, type AttendanceRecord } from '@/lib/api/attendance'
import { useOrg, useDevModeOverride, setDevModeOverride } from '@/lib/useOrg'

/** 기간 선택은 이 셋이면 충분하다. 달력 피커는 실제로 필요해지면 그때 */
const RANGES = [
  { days: 7, label: '최근 7일' },
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
] as const

function daysAgo(n: number): string {
  return kstDateStr(Date.now() - n * 24 * 60 * 60 * 1000)
}

/** KST 날짜별로 묶는다 — 하루에 여러 번 찍는 게 정상이다 (라이트는 N회 자유) */
function groupByDay(records: AttendanceRecord[]): [string, AttendanceRecord[]][] {
  const map = new Map<string, AttendanceRecord[]>()
  for (const r of records) {
    const key = kstDateStr(r.timestamp)
    const list = map.get(key)
    if (list) list.push(r)
    else map.set(key, [r])
  }
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]))
}

function RecordRow({ r }: { r: AttendanceRecord }) {
  // 부재는 type=ABSENCE 와 옛 형태(type=CHECKIN + manualReason) 둘 다 있다.
  // 공용 헬퍼가 그 둘을 함께 커버한다 — 여기서 다시 판별하지 않는다.
  const absence = isAbsenceRecord(r)

  return (
    <li className="flex items-start justify-between gap-3 px-4 py-3.5">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {absence ? (
            <Badge variant="purple">{manualReasonLabel(r.manualReason) || '부재'}</Badge>
          ) : (
            <Badge variant={r.type === 'CHECKIN' ? 'info' : 'default'}>
              {attendanceTypeLabel(r.type)}
            </Badge>
          )}
          {r.isManual && !absence && <Badge variant="outline">수기</Badge>}
          {r.manualStatus === 'PENDING' && <Badge variant="warning">승인 대기</Badge>}
          {r.manualStatus === 'REJECTED' && <Badge variant="destructive">반려</Badge>}
          {/*
            정정된 원본. 지우지 않고 남겨서 "무엇이 어떻게 바뀌었나" 를 볼 수 있게 한다.
            2026-07-15 에 숨김(supersede)을 폐기한 이유가 이것 — 숨기면 재출근이 사라져 보였다.
          */}
          {r.supersededById && <Badge variant="outline">정정됨</Badge>}
        </div>

        {/* 반려 사유·수기 메모는 잘라내지 않는다 (DCMC 16-1) */}
        {r.rejectionReason && (
          <p className="whitespace-pre-line text-xs text-[var(--color-danger)]">{r.rejectionReason}</p>
        )}
        {r.manualNote && <p className="whitespace-pre-line text-xs text-slate-400">{r.manualNote}</p>}
        {r.nfcSpot?.name && <p className="truncate text-xs text-slate-400">{r.nfcSpot.name}</p>}
        {/*
          야간조: 그날 출근 없이 퇴근만 있으면 본인도 "출근이 왜 없지" 로 헷갈린다.
          짝이라고 주장하지 않고 직전 기록이 무엇이었는지 사실만 덧붙인다.
        */}
        {r.prevTag && (
          <p className="text-xs text-slate-400">
            직전 기록 · {attendanceTypeLabel(r.prevTag.type)}{' '}
            {kstMonthDayStr(r.prevTag.timestamp)} {kstTimeStr(r.prevTag.timestamp)}
          </p>
        )}
      </div>

      {absence ? (
        <span className="shrink-0 text-sm text-slate-400">종일</span>
      ) : (
        <AttendanceTime at={new Date(r.timestamp)} kind={r.type as AttendanceKind} />
      )}
    </li>
  )
}

export default function Records() {
  const navigate = useNavigate()
  const [days, setDays] = useState<number>(7)
  const { data: org } = useOrg()
  const override = useDevModeOverride()

  const from = daysAgo(days)
  const to = kstDateStr()

  const { data, isLoading, error } = useQuery({
    queryKey: ['attendance', 'range', from, to],
    queryFn: async () => {
      const res = await getMyAttendance({ from, to, take: 300 })
      if (!res.success) throw new Error(res.error ?? '기록을 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  const grouped = data ? groupByDay(data) : []

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold">기록</h1>
        {org?.workStartTime && org?.workEndTime && (
          <p className="mt-1 text-sm text-slate-500">
            기준 {org.workStartTime} ~ {org.workEndTime}
          </p>
        )}
      </header>

      <Button variant="secondary" size="lg" className="w-full" onClick={() => navigate('/manual')}>
        직접 등록 (수기·부재)
      </Button>

      <div className="flex gap-2">
        {RANGES.map((r) => (
          <Button
            key={r.days}
            size="sm"
            variant={days === r.days ? 'default' : 'secondary'}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </Button>
        ))}
      </div>

      {import.meta.env.DEV && (
        <InfoBox tone="info" title="개발용 판정 모드">
          <p className="mb-2">판정 모드가 서버 설정으로 들어가는 건 S4입니다. 그전까지 로컬에서만 전환합니다.</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={override === 'STANDARD' ? 'default' : 'secondary'}
              onClick={() => setDevModeOverride('STANDARD')}
            >
              표준 (지각·조퇴 표시)
            </Button>
            <Button
              size="sm"
              variant={override === 'FACTUAL' || override === null ? 'default' : 'secondary'}
              onClick={() => setDevModeOverride('FACTUAL')}
            >
              비표준 (사실만)
            </Button>
          </div>
        </InfoBox>
      )}

      {isLoading && <LoadingState />}
      {error && <InfoBox tone="danger">{(error as Error).message}</InfoBox>}

      {!isLoading && !error && grouped.length === 0 && (
        <div className="rounded-card bg-card">
          <EmptyState title="이 기간에는 기록이 없습니다." hint="NFC 태그에 폰을 대면 기록됩니다." icon="clock" />
        </div>
      )}

      {grouped.map(([day, records]) => (
        <section key={day}>
          <h2 className="mb-2 text-sm font-bold text-slate-500">{kstMonthDayStr(day)}</h2>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {records.map((r) => (
              <RecordRow key={r.id} r={r} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
